import { useMemo, useState } from 'react';
import { Alert, Button, Spin, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { pointOnFeature, polygon } from '@turf/turf';
import { useTranslation } from 'react-i18next';
import { useAppState } from 'context/AppState';
import { Field } from 'base/types/RecommendationCropwise';
import { FieldConditionsRequest } from 'base/types/FieldConditions';
import { SoilColors } from 'utils/constants/SoilColors';
import { SoilTypeResponse } from 'base/types/SoilType';
import fieldIcon from 'assets/icons/field-conditions-field.svg';
import infoIcon from 'assets/icons/field-conditions-info.svg';
import { getTranslatedFieldName } from 'utils/constants/Fields';
import { getRankingCrop } from 'utils/getRankingCrop';
import { getBrazilCropSeason } from 'utils/brazilCropSeason';
import useMediaQuery from 'hooks/useMediaQuery';
import mobileFieldIcon from 'assets/icons/field-conditions-mobile-field.svg';
import expandUpIcon from 'assets/icons/field-conditions-expand-up.svg';
import expandDownIcon from 'assets/icons/field-conditions-expand-down.svg';
import useFieldConditions from './useFieldConditions';
import useFieldSoils from './useFieldSoils';
import useFieldElevation from './useFieldElevation';
import SoilBreakdown, { SoilClayEstimate } from './SoilBreakdown';
import ConditionsChart from './ConditionsChart';
import {
  ConditionsHeader,
  ConditionsSection,
  ConditionsTabs,
  ChartToolbar,
  FieldsTable,
  TableScroll,
  SoilLabel,
  LegendDot,
  EnvironmentalPanel,
  EnvironmentalHeading,
  FieldConditionsPanel,
  FieldIdentity,
  FieldArea,
  HeaderInfo,
  MobileFields,
  MobileField,
  MobileFieldMetrics,
  MobileFieldSoil,
  SoilSkeleton,
  ConditionsChartSkeleton,
} from './FieldConditions.styles';
import { seasonalRainfall, yearlyRainfall } from './weatherSeries';

function requestForField(
  field: Field | undefined,
  organizationId: string | undefined,
  plantingDate?: string,
  harvestDate?: string
): FieldConditionsRequest | null {
  if (!field || !organizationId || field.geometry.type !== 'Polygon') {
    return null;
  }
  try {
    const [lon, lat] = pointOnFeature(polygon(field.geometry.coordinates)).geometry.coordinates;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
      return null;
    }
    return {
      organizationId,
      lon,
      lat,
      plantingDate,
      harvestDate,
    };
  } catch (error: unknown) {
    console.warn('Unable to prepare field conditions request', error);
    return null;
  }
}

interface FieldRowProps {
  field: Field;
  isBrazil: boolean;
  soils: SoilTypeResponse | null;
  soilsLoading: boolean;
  mobile?: boolean;
  initiallyOpen?: boolean;
}

function FieldRow({
  field,
  isBrazil,
  soils,
  soilsLoading,
  mobile = false,
  initiallyOpen = false,
}: FieldRowProps) {
  const { t } = useTranslation();
  const elevation = useFieldElevation({ geometry: field.geometry });
  const soilKey = field.soilType?.replace(/:/g, '').toUpperCase();
  const soil = SoilColors.find((entry) => entry.key === soilKey || entry.label === soilKey);
  const area = field.declared_area ?? field.calculated_area;
  const fieldArea = Number.isFinite(area)
    ? `${area.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`
    : t('Not available');
  const displayName = getTranslatedFieldName({ defaultName: field.name.split('-@@@')[0], t });
  const cells = soils?.soilGrid[field.id];
  const identity = (
    <FieldIdentity>
      <img src={mobile ? mobileFieldIcon : fieldIcon} alt="" />
      <span>
        {displayName}
        <FieldArea>{fieldArea}</FieldArea>
      </span>
    </FieldIdentity>
  );
  const soilContent = (
    <>
      {soilsLoading ? (
        <SoilSkeleton role="status" aria-label={t('Loading soil data...')}>
          <span />
          <span />
        </SoilSkeleton>
      ) : soils && cells?.length ? (
        <SoilBreakdown
          cells={cells}
          resolution={soils.fieldsResolution}
          geometry={field.geometry}
          isBrazil={isBrazil}
        />
      ) : (
        <SoilLabel>
          <LegendDot $color={soil?.color ?? '#a3a9b9'} />
          {field.soilType ? t(field.soilType) : t('Not available')}
          {isBrazil && field.soilType && <SoilClayEstimate texture={field.soilType} />}
        </SoilLabel>
      )}
    </>
  );
  const elevationContent = (
    <>
      {elevation.loading ? (
        <Spin size="small" aria-label={t('Loading elevation')} />
      ) : elevation.meters !== null ? (
        `${elevation.meters.toLocaleString()} m`
      ) : elevation.error ? (
        <>
          <span role="alert">{t('Elevation unavailable')}</span>
          <Button size="small" type="link" onClick={elevation.retry}>
            {t('Retry elevation')}
          </Button>
        </>
      ) : (
        t('Not available')
      )}
    </>
  );
  if (mobile) {
    return (
      <MobileField open={initiallyOpen}>
        <summary aria-label={t('{{field}} conditions', { field: displayName })}>
          {identity}
          <img className="expanded-chevron" src={expandUpIcon} alt="" />
          <img className="collapsed-chevron" src={expandDownIcon} alt="" />
        </summary>
        <MobileFieldMetrics>
          <dt>{t('Elevation')}</dt>
          <dd>{elevationContent}</dd>
        </MobileFieldMetrics>
        <MobileFieldSoil>
          <div>{t('Soil Type')}</div>
          {soilContent}
        </MobileFieldSoil>
      </MobileField>
    );
  }
  return (
    <tr>
      <th scope="row">{identity}</th>
      <td>{fieldArea}</td>
      <td>{soilContent}</td>
      <td>{elevationContent}</td>
    </tr>
  );
}

export default function FieldConditions() {
  const { t } = useTranslation();
  const mobile = useMediaQuery('(max-width: 600px)');
  const {
    apiData: { recommendationCropwise: recommendation },
  } = useAppState();
  const [fieldId, setFieldId] = useState('');
  const [mode, setMode] = useState<'monthly' | 'yearly'>('monthly');
  const current = recommendation?.recommendations[0];
  const fields = useMemo(
    () =>
      (current?.multi_field.geometry.properties ?? []).flatMap((property) =>
        property.fields.map((field) => ({ field, property }))
      ),
    [current]
  );
  const selected = fields.find((entry) => entry.field.id === fieldId) ?? fields[0];
  const inputs = current?.multi_field.config.agronomicInputs;
  const organizationId = selected?.property.org_id || recommendation?.organization_id;
  const request = requestForField(
    selected?.field,
    organizationId,
    inputs?.plantingDate || undefined,
    inputs?.harvestDate || undefined
  );
  const { data, loading, error, retry } = useFieldConditions(request);
  const soilFields = useMemo(
    () =>
      fields
        .map(({ field }) => ({
          id: field.id,
          acres: field.declared_area ?? field.calculated_area,
          geometry: field.geometry,
        }))
        .filter((field) => field.geometry.type === 'Polygon'),
    [fields]
  );
  const soils = useFieldSoils({
    fields: soilFields,
    cropRegion:
      inputs && current
        ? getRankingCrop(recommendation?.country ?? '', current.crop_type ?? '', inputs)
        : undefined,
  });
  const weather = data?.weather;
  const isBrazil = recommendation?.country?.toLowerCase() === 'brazil';
  const impact = isBrazil ? data?.oni?.impact : undefined;
  const seasonName = getBrazilCropSeason(recommendation?.country, current?.crop_id);
  const seasonTotal = weather ? seasonalRainfall(weather, seasonName) : null;
  const years = weather ? yearlyRainfall(weather, data?.oni ?? null) : [];
  const partialError =
    data && Object.keys(data.sourceErrors).some((source) => source !== 'elevation');

  if (!recommendation) {
    return null;
  }
  const fieldRows = fields.map(({ field, property }, index) => {
    return (
      <FieldRow
        key={`${property.id}-${field.id}`}
        field={field}
        isBrazil={isBrazil}
        soils={soils.data}
        soilsLoading={soils.loading}
        mobile={mobile}
        initiallyOpen={index === 0}
      />
    );
  });
  return (
    <ConditionsSection id="field-environmental-conditions">
      <FieldConditionsPanel as="section" aria-labelledby="diagnostic-field-conditions-title">
        <h2 id="diagnostic-field-conditions-title">{t('Field Conditions')}</h2>
        <p>{t('Soil and elevation for each field, and how they shaped the seeds we picked')}</p>
        {soils.error && (
          <ConditionsHeader>
            <Alert type="warning" message={t('Soil distribution could not be loaded')} />
            <Button onClick={soils.retry}>{t('Retry soil data')}</Button>
          </ConditionsHeader>
        )}
        {fields.length ? (
          mobile ? (
            <MobileFields aria-label={t('Field Conditions')}>{fieldRows}</MobileFields>
          ) : (
            <TableScroll>
              <FieldsTable aria-label={t('Field Conditions')}>
                <thead>
                  <tr>
                    <th scope="col">{t('Field')}</th>
                    <th scope="col">{t('Area')}</th>
                    <th scope="col">
                      {t('Soil Type')}{' '}
                      <Tooltip title={t('Soil classification for the field')}>
                        <HeaderInfo src={infoIcon} alt={t('About soil type')} />
                      </Tooltip>
                    </th>
                    <th scope="col">
                      {t('Elevation')}
                    </th>
                  </tr>
                </thead>
                <tbody>{fieldRows}</tbody>
              </FieldsTable>
            </TableScroll>
          )
        ) : (
          <Alert type="info" message={t('No fields available')} />
        )}
      </FieldConditionsPanel>
      <EnvironmentalPanel as="section" aria-labelledby="diagnostic-environmental-conditions-title">
        <ConditionsHeader>
          <EnvironmentalHeading>
            <h2 id="diagnostic-environmental-conditions-title">{t('Environmental conditions')}</h2>
            <p>
              {t(
                'Rainfall is expected to be close to the historical average for the north/ northeast'
              )}
            </p>
          </EnvironmentalHeading>
          {fields.length > 1 && (
            <label>
              {t('Field')}
              <select
                aria-label={t('Field')}
                value={selected?.field.id ?? ''}
                onChange={(event) => setFieldId(event.target.value)}
              >
                {fields.map(({ field: option, property }) => (
                  <option key={`${property.id}-${option.id}`} value={option.id}>
                    {property.name} · {option.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </ConditionsHeader>
        {fields.length > 0 && !request && (
          <Alert type="info" message={t('Field location or organization unavailable')} />
        )}
        {loading && (
          <div
            role="status"
            aria-label={t('Loading environmental conditions...')}
            aria-busy="true"
            data-testid="conditions-chart-skeleton"
          >
            <ConditionsChartSkeleton active />
          </div>
        )}
        {(error || partialError) && (
          <ConditionsHeader>
            <Alert type="warning" message={t('Some conditions could not be loaded')} />
            <Button
              aria-label={t('Retry')}
              icon={<ReloadOutlined aria-hidden="true" />}
              onClick={retry}
            >
              {t('Retry')}
            </Button>
          </ConditionsHeader>
        )}
        {!weather && impact && <h3>{t(impact.title)}</h3>}
        {weather ? (
          <>
            {!data?.oni && <Alert type="info" message={t('ENSO history unavailable')} />}
            <ChartToolbar>
              <ConditionsTabs role="tablist" aria-label={t('Rainfall period')}>
                <button
                  type="button"
                  role="tab"
                  id="conditions-monthly-tab"
                  aria-controls="conditions-rainfall-panel"
                  aria-selected={mode === 'monthly'}
                  onClick={() => setMode('monthly')}
                >
                  {t('Annually')}
                </button>
                <button
                  type="button"
                  role="tab"
                  id="conditions-yearly-tab"
                  aria-controls="conditions-rainfall-panel"
                  aria-selected={mode === 'yearly'}
                  onClick={() => setMode('yearly')}
                >
                  {t('Yearly')}
                </button>
              </ConditionsTabs>
              <small>
                {mode === 'monthly' ? (
                  seasonTotal === null ? (
                    t('Total seasonal rainfall {{season}}: Not available', {
                      season: t(seasonName ?? weather.season.label),
                    })
                  ) : (
                    t(
                      'Total seasonal rainfall {{season}}, {{rainfall}}mm: Last {{years}} years average',
                      {
                        season: t(seasonName ?? weather.season.label),
                        rainfall: Math.round(seasonTotal),
                        years: weather.years,
                      }
                    )
                  )
                ) : (
                  <>
                    {t('Total seasonal rainfall')} · {weather.season.label} ·{' '}
                    {years.length
                      ? `${years[0].label} – ${years[years.length - 1].label}`
                      : t('Not available')}
                  </>
                )}
              </small>
            </ChartToolbar>
            <div
              id="conditions-rainfall-panel"
              role="tabpanel"
              aria-labelledby={`conditions-${mode}-tab`}
            >
              <ConditionsChart
                weather={weather}
                oni={data?.oni ?? null}
                mode={mode}
                seasonName={seasonName}
              />
            </div>
          </>
        ) : (
          !loading && <Alert type="info" message={t('Historical weather unavailable')} />
        )}
      </EnvironmentalPanel>
    </ConditionsSection>
  );
}
